import { Email, Password, Required } from '@wikus-ui/components/utils/validators';
import { OnSubmitHandler, useInputGroup } from '@wikus-ui/hooks/useInputGroup';
import { WikusButton, WikusButtonRow } from '@wikus-ui/components/core/button';
import { WikusInputRow, WikusTextField } from '@wikus-ui/components/core/input';
import { WikusPanel } from '@wikus-ui/components/core/card';
import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import * as CustomerUserActions from './../../../actions/customer-user';
import {
  CreateCustomerUser,
  CustomerUserActions as CustomerUserActionsModel,
  EditCustomerUser
} from './../../../model/customer-user';
import { useActions } from './../../../actions';

import { CustomerRouteParams } from '../../../pages/customer/models';
import { WikusSelect } from '@wikus-ui/components/core/select';
import { useParams } from 'react-router-dom';

import * as CustomerActions from '../../../actions/customer';
import * as UserActions from '../../../actions/user';
import { CustomerInput } from '../storage/dashboard/customer-input';
import { RootState } from '../../../reducers';
import { User } from '../../../model';
import {
  UserActions as UserActionsModel
} from './../../../model/user';
import { useSelector } from 'react-redux';

type TCreateCustomerUserFormProps = {
  value?: User;
  onCreate?: () => void;
  isSupport?: boolean;
  onClear?: () => void;
};

export const CreateCustomerUserForm = ({
  value: initialValue,
  isSupport,
  onCreate,
  onClear,
}: TCreateCustomerUserFormProps) => {
  const { id: customerId } = useParams<CustomerRouteParams>();
  const intl = useIntl();
  const CreateCustomerUserInputGroup = `create-user-${customerId}`;
  const customerActions: typeof CustomerActions = useActions(CustomerActions);
  const userActions: typeof UserActions = useActions(UserActions);
  const customers = useSelector((state: RootState) => state.customers);

  useEffect(() => {
    if (isSupport) {
      customerActions.fetchInitial({ existing: true })
      valueChanged('role', 'S');
    }
  }, [isSupport])

  const customerAction = useCallback(async (value: any) => {
    if (customerId && !initialValue) {
      const { type } = await customerUserActions.createCustomerUser(
        {
          ...value,
        } as unknown as CreateCustomerUser,
        customerId
      );

      return type === CustomerUserActionsModel.CREATE_CUSTOMER_USER_SUCCESS;
    } else if (customerId && !!initialValue) {
      const { type } = await customerUserActions.updateCustomerUser(
        {
          ...value,
        } as unknown as EditCustomerUser,
        customerId,
        initialValue.id
      );

      return type === CustomerUserActionsModel.UPDATE_CUSTOMER_USER_SUCCESS;
    } else if (!customerId && !initialValue) {
      const { type } = await userActions.createAdminUser(
        {
          ...value,
        } as unknown as User
      );

      return type === UserActionsModel.CREATE_ADMIN_USER_SUCCESS;
    } else if (!customerId && !!initialValue) {
      const { type } = await userActions.updateAdminUser(
        {
          ...value,
        } as unknown as User,
        initialValue.id
      );

      return type === UserActionsModel.UPDATE_ADMIN_USER_SUCCESS;
    }
  }, [initialValue, customerId])

  const customerUserActions: typeof CustomerUserActions =
    useActions(CustomerUserActions);

  const _onSubmit: OnSubmitHandler = async () => {
    if (!isValid) {
      return;
    }

    const isSuccess = await customerAction(value)

    if (isSuccess) {
      clearForm();
      onCreate && onCreate();
    }

    return isSuccess;
  }

  const { value, isValid, clear, onSubmit, createInputProps, isSubmitting, valueChanged, setGroup } =
    useInputGroup(CreateCustomerUserInputGroup, _onSubmit, 'create-user');
  const passwordValidator = useCallback(
    (password: unknown) => {
      if (!!initialValue) {
        return undefined;
      }

      return Password([value?.firstName, value?.lastName])(password);
    },
    [value?.firstName, value?.lastName, initialValue]
  );

  useEffect(() => {
    if (initialValue) {
      setGroup({
        submitted: false,
        value: initialValue,
      });
      valueChanged('password', undefined, undefined);
      if (isSupport) {
        valueChanged('role', 'S');
      }
    }
  }, [initialValue])

  const clearForm = useCallback(() => {
    clear();
    onClear?.();

    if (isSupport) {
      valueChanged('role', 'S');
    }
  }, [onClear, clear])

  const options = !isSupport ? [
    {
      id: 'U',
      name: intl.formatMessage({ id: 'roles.U' }),
    },
    {
      id: 'CA',
      name: intl.formatMessage({ id: 'roles.CA' }),
    }
  ] : [{
    id: 'S',
    name: intl.formatMessage({ id: 'roles.S' }),
  }];

  console.log('isValid', isValid)

  // if (user.role === 'A') {
  //   options.push({
  //     id: 'A',
  //     name: intl.formatMessage({ id: 'roles.A' }),
  //   });
  // }

  const mustHaveEmail = value?.role !== 'U';

  const onChangeSupportCustomers = useCallback((_, customers) => {
    valueChanged('supportCustomers', customers)
  }, [valueChanged])

  return (
    <WikusPanel
      title={intl.formatMessage({ id: initialValue ? 'customers.tabs.users.update' : 'customers.tabs.users.create' })}
    >
      <form onSubmit={onSubmit}>
        <WikusInputRow>
          <WikusTextField {...createInputProps('firstName')} xs={6} />
          <WikusTextField {...createInputProps('lastName')} xs={6} />
        </WikusInputRow>
        <WikusInputRow>
          <WikusTextField {...createInputProps('email', mustHaveEmail ? Email : Required)} xs={6} />
          {!initialValue ? <WikusTextField
            {...createInputProps('password')}
            validator={passwordValidator}
            type="password"
            xs={6}
          /> : undefined}
        </WikusInputRow>
        <WikusInputRow>
          <WikusSelect
            {...createInputProps('role')}
            options={options}
          />
        </WikusInputRow>
        {(isSupport && value?.role === 'S') ? (<WikusInputRow>
          <CustomerInput options={customers ?? []} value={value?.supportCustomers ?? []} onChange={onChangeSupportCustomers} />
        </WikusInputRow>) : null}
        <WikusButtonRow>
          <WikusButton onClick={clearForm} type="button" color="secondary" disabled={isSubmitting}>
            {intl.formatMessage({
              id: 'create-user.abort',
            })}
          </WikusButton>
          <WikusButton type="submit" color="primary" disabled={isSubmitting}>
            {intl.formatMessage({
              id: 'create-user.submit',
            })}
          </WikusButton>
        </WikusButtonRow>
      </form>
    </WikusPanel>
  );
};
